// Cancellation context helpers.
//
// Base44 doesn't expose a dedicated `cancel_reason` field. We compose a
// human-readable reason from the signals that are exposed: external_status
// (verbatim Base44 status), payment_status, and free-text notes.
//
// Key business rule: status === "cancelled" + payment_status === "paid"
// means a refund is owed. The UI surfaces this prominently.

const PAID_PAYMENT_STATUSES = new Set(["paid", "completed", "captured"]);

/** True when the order has been cancelled AND money has already changed hands. */
export function isPaidCancellation(order) {
  if (!order || order.status !== "cancelled") return false;
  const pay = String(order.payment_status || "").toLowerCase();
  return PAID_PAYMENT_STATUSES.has(pay);
}

/**
 * Returns a one-line human reason explaining why an order is cancelled.
 * Returns null for orders that aren't cancelled.
 */
export function cancellationReason(order) {
  if (!order || order.status !== "cancelled") return null;
  const pay = String(order.payment_status || "").toLowerCase();
  const ext = order.external_status || "cancelled";
  const notes = (order.notes || "").trim();

  let lead;
  if (pay === "failed") lead = "Cancelled · payment failed";
  else if (PAID_PAYMENT_STATUSES.has(pay)) lead = "⚠ REFUND NEEDED · already paid";
  else if (pay) lead = `Cancelled · payment ${pay}`;
  else lead = `Cancelled · ${ext}`;

  return notes ? `${lead} — ${notes}` : lead;
}

/** Short label rendered on list cards ("Cancelled" or "Refund needed"). */
export function cancellationShortLabel(order) {
  if (!order || order.status !== "cancelled") return null;
  return isPaidCancellation(order) ? "Refund needed" : "Cancelled";
}
